import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, Loader2 } from 'lucide-react'

import { MinimalInput } from '@/components/ui/MinimalInput'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { COUNTRIES } from '@/lib/countries'

const theseusLogo = '/theseus-logo.png'

function safeNextPath(value: string | null): string {
  return value?.startsWith('/') && !value.startsWith('//') ? value : '/'
}

export function AcceptInviteForm() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const nextPath = safeNextPath(searchParams.get('next'))
  const countryOptions = useMemo(() => COUNTRIES, [])

  // Captured during render, before the effect below strips them from the URL.
  // Reading inside the effect would break under StrictMode, whose second pass
  // would find the URL already cleaned.
  //
  // Deliberately has no setter. Clearing it would re-render with both `user`
  // and the token empty — `user` only arrives asynchronously via
  // onAuthStateChange — flashing "Invitation link unavailable" over a setup
  // that is actually succeeding. Consumption is tracked in a ref instead.
  const [pendingToken] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('token_hash')
  )
  const tokenConsumedRef = useRef(false)
  const [otpType] = useState<'invite' | 'magiclink'>(() =>
    new URLSearchParams(window.location.search).get('type') === 'magiclink' ? 'magiclink' : 'invite'
  )

  useEffect(() => {
    // Deliberately not verified here. A scanner prefetching this page would
    // spend the token before the invitee ever arrives, which is the failure
    // this flow exists to prevent. Verification happens on submit.
    if (!pendingToken) return
    const params = new URLSearchParams(window.location.search)
    params.delete('token_hash')
    params.delete('type')
    // Keep `next` - it drives where we land after setup.
    const query = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
  }, [pendingToken])

  const [fullName, setFullName] = useState('')
  const [organization, setOrganization] = useState('')
  const [role, setRole] = useState('')
  const [country, setCountry] = useState('')
  const [signalContact, setSignalContact] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A pending token stands in for a session: the form renders unverified and
  // submitting establishes the session.
  if (!user && !pendingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-4">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
          <h1 className="text-xl text-foreground">Invitation link unavailable</h1>
          <p className="text-sm text-muted-foreground">
            This invitation may have expired. Ask the sender to resend it.
          </p>
          <Link to="/login" className="text-sm text-primary hover:underline">
            Return to sign in
          </Link>
        </div>
      </div>
    )
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    if (!fullName.trim() || !organization.trim() || !role.trim() || !country) {
      setError('Complete all required profile fields.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setSubmitting(true)

    // Spend the token here, so it is consumed by a person completing the form
    // rather than by whatever fetched the URL first.
    if (pendingToken && !tokenConsumedRef.current) {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: pendingToken,
        type: otpType,
      })
      if (verifyError) {
        setSubmitting(false)
        setError('This invitation link has expired or already been used. Ask the sender to resend it.')
        return
      }
      // A retry after a later failure must not re-verify a spent token.
      tokenConsumedRef.current = true
    }

    const { error: updateError } = await supabase.auth.updateUser({
      password,
      data: {
        full_name: fullName.trim(),
        organization: organization.trim(),
        role: role.trim(),
        country,
        signal_contact: signalContact.trim() || undefined,
      },
    })
    setSubmitting(false)

    if (updateError) {
      setError(updateError.message)
      return
    }
    navigate(nextPath, { replace: true })
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <a href="https://theseus.us" className="fixed top-6 left-6" target="_blank" rel="noreferrer">
        <img src={theseusLogo} alt="Theseus" className="h-6" />
      </a>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-lg">
          <div className="mb-10 wizard-fade-in">
            <h1 className="mb-2 text-2xl text-foreground">Set up your account</h1>
            <p className="text-sm text-muted-foreground">
              {user ? `Invited as ${user.email}` : 'Complete the form below to activate your account.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <InviteInput
                label="Full name *"
                value={fullName}
                onChange={setFullName}
                autoComplete="name"
                placeholder="John Doe"
                delay={50}
              />
              <InviteInput
                label="Organization *"
                value={organization}
                onChange={setOrganization}
                autoComplete="organization"
                placeholder="Your company"
                delay={100}
              />
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <InviteInput
                label="Role *"
                value={role}
                onChange={setRole}
                autoComplete="organization-title"
                placeholder="Your role"
                delay={150}
              />
              <div className="space-y-2 wizard-stagger" style={{ animationDelay: '200ms' }}>
                <label htmlFor="invite-country" className="block text-sm text-muted-foreground/80">
                  Country *
                </label>
                <select
                  id="invite-country"
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                  className="w-full border-0 border-b border-border/40 bg-transparent pb-2 text-base text-foreground transition-colors focus:border-foreground/60 focus:outline-none focus:ring-0"
                >
                  <option value="">Select country</option>
                  {countryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <InviteInput
              label="Signal contact (optional)"
              value={signalContact}
              onChange={setSignalContact}
              placeholder="Phone number or username"
              delay={250}
            />

            <div className="my-2 border-t border-border/20" />

            <InviteInput
              label="Password *"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              placeholder="At least 8 characters"
              delay={300}
            />
            <InviteInput
              label="Confirm password *"
              type="password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              placeholder="Re-enter your password"
              delay={350}
            />

            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive wizard-fade-in">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Completing setup...
                  </span>
                ) : (
                  'Complete setup'
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}

function InviteInput({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  placeholder,
  delay,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  autoComplete?: string
  placeholder?: string
  delay?: number
}) {
  return (
    <MinimalInput
      label={label}
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      autoComplete={autoComplete}
      placeholder={placeholder}
      delay={delay}
      required={!label.includes('optional')}
    />
  )
}
